import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../../utils/Api';
import { showError, showSuccess } from '../../../utils/Toast';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../../../Styles/Login.css";
import { getAuthReturnTo, openAuthModal } from "../../../utils/authModal";
import { completeBuyerAuthentication } from "../../../utils/buyerAuthFlow";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&]).{8,}$/;
const PASSWORD_VALIDATION_MESSAGE =
    'Password must be at least 8 characters and include letters, numbers and special characters';

const Register = () => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',
        roleid: 3,
    });

    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isEmailOtpSent, setIsEmailOtpSent] = useState(false);
    const [isEmailVerified, setIsEmailVerified] = useState(false);
    const [otp, setOtp] = useState('');
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
    const [otpSessionToken, setOtpSessionToken] = useState('');
    const [verificationToken, setVerificationToken] = useState('');

    const resetEmailVerificationState = () => {
        setIsEmailOtpSent(false);
        setIsEmailVerified(false);
        setOtp('');
        setOtpSessionToken('');
        setVerificationToken('');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));

        if (name === 'email' && value !== formData.email) {
            resetEmailVerificationState();
        }

        if (errors[name] || errors.submit || errors.otp) {
            setErrors((prev) => ({
                ...prev,
                [name]: '',
                submit: '',
                otp: '',
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.username.trim()) {
            newErrors.username = 'Username is required';
        } else if (formData.username.trim().length < 3) {
            newErrors.username = 'Username must be at least 3 characters';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!EMAIL_REGEX.test(formData.email.trim())) {
            newErrors.email = 'Email is invalid';
        } else if (!isEmailVerified || !verificationToken) {
            newErrors.email = 'Please verify your email before creating account';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (!PASSWORD_REGEX.test(formData.password)) {
            newErrors.password = PASSWORD_VALIDATION_MESSAGE;
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Confirm password is required';
        } else if (formData.confirmPassword !== formData.password) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        return newErrors;
    };

    const handleSendOtp = async () => {
        const trimmedEmail = formData.email.trim().toLowerCase();

        if (!trimmedEmail) {
            setErrors((prev) => ({
                ...prev,
                email: 'Email is required',
            }));
            showError('Please enter your email address');
            return;
        }

        if (!EMAIL_REGEX.test(trimmedEmail)) {
            setErrors((prev) => ({
                ...prev,
                email: 'Email is invalid',
            }));
            showError('Please enter a valid email address');
            return;
        }

        setIsSendingOtp(true);

        try {
            const response = await axios.post(`${API_URL.api}/register/send-otp`, {
                email: trimmedEmail,
            });

            const nextOtpSessionToken = response?.data?.otp_session_token;

            if (!nextOtpSessionToken) {
                throw new Error('OTP session token missing from response');
            }

            setOtpSessionToken(nextOtpSessionToken);
            setVerificationToken('');
            setOtp('');
            setIsEmailOtpSent(true);
            setIsEmailVerified(false);
            setErrors((prev) => ({
                ...prev,
                email: '',
                otp: '',
                submit: '',
            }));
            showSuccess(response?.data?.message || 'OTP sent successfully');
        } catch (error) {
            const message =
                error.response?.data?.message ||
                error.message ||
                'Failed to send OTP';

            setIsEmailOtpSent(false);
            setOtpSessionToken('');
            setVerificationToken('');
            setErrors((prev) => ({
                ...prev,
                submit: message,
            }));
            showError(message);
        } finally {
            setIsSendingOtp(false);
        }
    };

    const handleVerifyOtp = async () => {
        const trimmedEmail = formData.email.trim().toLowerCase();
        const trimmedOtp = otp.trim();

        if (!isEmailOtpSent || !otpSessionToken) {
            const message = 'Please send an OTP to your email first';
            setErrors((prev) => ({
                ...prev,
                otp: message,
            }));
            showError(message);
            return;
        }

        if (!/^\d{6}$/.test(trimmedOtp)) {
            const message = 'Enter a valid 6 digit OTP';
            setErrors((prev) => ({
                ...prev,
                otp: message,
            }));
            showError(message);
            return;
        }

        setIsVerifyingOtp(true);

        try {
            const response = await axios.post(`${API_URL.api}/register/verify-otp`, {
                email: trimmedEmail,
                otp: trimmedOtp,
                otp_session_token: otpSessionToken,
            });

            const nextVerificationToken = response?.data?.verification_token;

            if (!nextVerificationToken) {
                throw new Error('Verification token missing from response');
            }

            setVerificationToken(nextVerificationToken);
            setIsEmailVerified(true);
            setIsEmailOtpSent(false);
            setOtp('');
            setOtpSessionToken('');
            setErrors((prev) => ({
                ...prev,
                email: '',
                otp: '',
                submit: '',
            }));
            showSuccess(response?.data?.message || 'Email verified successfully');
        } catch (error) {
            const message =
                error.response?.data?.message ||
                error.message ||
                'OTP verification failed';

            setErrors((prev) => ({
                ...prev,
                otp: message,
            }));
            showError(message);
        } finally {
            setIsVerifyingOtp(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = validateForm();

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            if (newErrors.email) {
                showError(newErrors.email);
            }
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios.post(`${API_URL.api}/register`, {
                username: formData.username.trim(),
                email: formData.email.trim().toLowerCase(),
                password: formData.password,
                phone: formData.phone,
                roleid: formData.roleid,
                verification_token: verificationToken,
            });

            if (response.status === 200 || response.status === 201) {
                if (response?.data?.token) {
                    await completeBuyerAuthentication(response.data, {
                        successMessage: response?.data?.message || 'Registration successful',
                    });
                } else {
                    showSuccess(response?.data?.message || 'Registration successful');
                    openAuthModal();
                }
            } else {
                const message = response.data.message || "Registration failed";
                setErrors({
                    submit: message,
                });
                showError(message);
            }
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || "Registration failed";

            setErrors({
                submit: message,
            });
            showError(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePhoneChange = (value, country) => {
        const dialCode = country.dialCode;
        const phoneNumberWithoutCode = value.slice(dialCode.length);
        const formattedPhone = `+${dialCode} ${phoneNumberWithoutCode}`.trim();

        setFormData((prev) => ({
            ...prev,
            phone: formattedPhone,
        }));

        if (errors.phone) {
            setErrors((prev) => ({
                ...prev,
                phone: "",
            }));
        }
    };

    return (
        <div className="register-container">
            <div className="register-shell">
                <div className="register-visual-panel">
                    <img
                        src="/assets/signup-medical-platform.svg"
                        alt=""
                        aria-hidden="true"
                        className="register-visual-background"
                    />
                    <div className="register-visual-overlay" />
                    <div className="register-visual-content">
                        <div className="register-brand-row">
                            <img
                                src="/elumialogo.jpg"
                                alt="Elumia MedStore"
                                className="register-brand-logo"
                            />
                            {/* <span className="register-brand-label">Elumia MedStore</span> */}
                        </div>

                        <div className="register-visual-copy">
                            <p className="register-overline">Trusted Medical Ordering</p>
                            <h2>Create your Elumia account.</h2>
                            {/* <p className="register-visual-text">
                                Verified medical supply ordering with a cleaner, faster path to
                                everyday restocking.
                            </p> */}
                        </div>
                    </div>
                </div>

                <div className="register-card register-form-panel">
                    <div className="register-form-frame">
                        <div className="register-header">
                            <h2>Create Account</h2>
                            <p>Join us today! Fill in your details to get started.</p>
                        </div>

                        <form onSubmit={handleSubmit} className="register-form">
                            <div className="form-group">
                                <label htmlFor="username">
                                    <i className="fas fa-user"></i> Username
                                </label>
                                <input
                                    type="text"
                                    id="username"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    placeholder="Enter your username"
                                    className={errors.username ? 'error' : ''}
                                />
                                {errors.username && <span className="error-message">{errors.username}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">
                                    <i className="fas fa-envelope"></i> Email
                                </label>
                                <div className="register-email-row">
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Enter your email"
                                        className={errors.email ? 'error' : ''}
                                    />
                                    <button
                                        type="button"
                                        className="register-verify-button"
                                        onClick={handleSendOtp}
                                        disabled={isSendingOtp || isEmailVerified || !EMAIL_REGEX.test(formData.email.trim())}
                                    >
                                        {isSendingOtp ? 'Sending...' : isEmailVerified ? 'Verified' : 'Verify'}
                                    </button>
                                </div>
                                {isEmailVerified ? (
                                    <span className="register-email-status register-email-status--verified">
                                        <i className="fas fa-check-circle" aria-hidden="true"></i>
                                        Email verified
                                    </span>
                                ) : (
                                    <span className="register-email-status register-email-status--pending">
                                        Verify your email before creating your account.
                                    </span>
                                )}
                                {errors.email && <span className="error-message">{errors.email}</span>}

                                {isEmailOtpSent && (
                                    <div className="register-otp-panel">
                                        <label htmlFor="register-email-otp">Enter OTP sent to your email</label>
                                        <div className="register-otp-actions">
                                            <input
                                                type="text"
                                                id="register-email-otp"
                                                value={otp}
                                                onChange={(e) => {
                                                    setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                                                    if (errors.otp) {
                                                        setErrors((prev) => ({
                                                            ...prev,
                                                            otp: '',
                                                        }));
                                                    }
                                                }}
                                                placeholder="Enter OTP"
                                                className={errors.otp ? 'error register-otp-input' : 'register-otp-input'}
                                                maxLength="6"
                                            />
                                            <button
                                                type="button"
                                                className="register-otp-button"
                                                onClick={handleVerifyOtp}
                                                disabled={isVerifyingOtp}
                                            >
                                                {isVerifyingOtp ? 'Verifying...' : 'Verify OTP'}
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            className="register-otp-resend"
                                            onClick={handleSendOtp}
                                            disabled={isSendingOtp}
                                        >
                                            {isSendingOtp ? 'Sending...' : 'Resend OTP'}
                                        </button>
                                        {errors.otp && <span className="error-message">{errors.otp}</span>}
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label>Phone Number</label>
                                <PhoneInput
                                    country={"ae"}
                                    value={formData.phone}
                                    disableDropdown={true}
                                    onChange={handlePhoneChange}
                                    enableSearch
                                    preferredCountries={["ae", "in", "us", "gb"]}
                                    inputStyle={{
                                        width: "100%",
                                        height: "45px",
                                        borderRadius: "10px",
                                        backgroundColor: "#fff",
                                        border: "1px solid var(--color-border)",
                                        color: "var(--color-text)",
                                    }}
                                />
                                {errors.phone && (
                                    <span className="error-message">{errors.phone}</span>
                                )}
                            </div>

                            <div className="register-password-grid">
                                <div className="form-group">
                                    <label htmlFor="password">
                                        <i className="fas fa-lock"></i> Password
                                    </label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder="Enter your password"
                                        className={errors.password ? 'error' : ''}
                                        autoComplete="new-password"
                                    />
                                    {errors.password && <span className="error-message">{errors.password}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="confirmPassword">
                                        <i className="fas fa-lock"></i> Confirm Password
                                    </label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        placeholder="Confirm your password"
                                        className={errors.confirmPassword ? 'error' : ''}
                                        autoComplete="new-password"
                                    />
                                    {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                                </div>
                            </div>

                            {errors.submit && <div className="submit-error">{errors.submit}</div>}

                            <button
                                type="submit"
                                className="register-button"
                                disabled={isLoading || !isEmailVerified}
                            >
                                {isLoading ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i> Creating Account...
                                    </>
                                ) : (
                                    'Create Account'
                                )}
                            </button>

                            <div className="login-link">
                                Already have an account?{" "}
                                <a
                                    href="/login"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        const returnTo = getAuthReturnTo();
                                        openAuthModal(returnTo ? { returnTo } : {});
                                    }}
                                >
                                    Sign In
                                </a>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Register;
